import { type MutableRefObject, useCallback } from 'react'
import { toast } from 'sonner'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import {
  notifyFrozenDeleteBlocked,
  notifyFrozenGroupChildDeleteBlocked,
} from '../handlers/frozen-delete-feedback'
import { selectionHasChildUnderFrozenGroup } from '../helpers/frozen-group-child'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

const LOCKED_DELETE_MESSAGE = 'Object Locked: Can’t delete a locked object'

/**
 * Object-level Delete for every entry point (keyboard, Edit menu, both object context menus):
 * refused mid text edit, for a locked or frozen selection, and for a frozen group's child inside
 * group isolation; otherwise announced as one gesture and removed as one undo step.
 */
export function useDeleteSelection({
  internalShapeManagerRef,
  canSelectShapes,
  activeIsolationGroupId,
  isTextEditSessionActive,
  getCurrentSelectionSnapshots,
  buildDeleteGestureScope,
  pulseSelectionGeometryBlocked,
  onDeleteGestureScope,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  canSelectShapes: boolean
  activeIsolationGroupId: string | null
  isTextEditSessionActive: () => boolean
  getCurrentSelectionSnapshots: () => { shapeSnapshots: ShapeData[]; textSnapshots: TextBoxData[] }
  buildDeleteGestureScope: (
    shapeSnapshots: readonly { id: string }[],
    textSnapshots: readonly { id: string }[]
  ) => { txnId: string; ids: string[] }
  pulseSelectionGeometryBlocked: () => void
  onDeleteGestureScope: CenterCanvasConfig['onDeleteGestureScope']
}) {
  const deleteSelectedObjects = useCallback(
    (opts?: { textOnly?: boolean }): 'none' | 'blocked' | 'deleted' => {
      // A live text-edit session owns Delete: the frame being edited must never be removed by an
      // object-level delete, whichever entry point asks (WMPCM-7044). Guarding here rather than at
      // each call site covers the keyboard, `deleteSelection` (the Edit menu), and both object
      // context menus in one place. `enterEditMode` forces the selection to the editing box, so
      // without this the selection handed to the delete below *is* that frame.
      //
      // Deliberately 'none', not 'blocked': 'blocked' means "refused, with a toast/pulse", and a
      // keystroke that the text editor is about to handle must not raise one.
      if (isTextEditSessionActive()) return 'none'

      const { shapeSnapshots, textSnapshots } = getCurrentSelectionSnapshots()
      const hasLockedShape =
        !opts?.textOnly && shapeSnapshots.some((shape) => shape.locked === true)
      const hasLockedText = textSnapshots.some((box) => box.locked === true)

      if (hasLockedShape || hasLockedText) {
        toast.error(LOCKED_DELETE_MESSAGE)
        return 'blocked'
      }

      const hasSelfFrozenShape =
        !opts?.textOnly && shapeSnapshots.some((shape) => shape.frozen === true)
      const hasSelfFrozenText = textSnapshots.some((box) => box.frozen === true)

      if (hasSelfFrozenShape || hasSelfFrozenText) {
        notifyFrozenDeleteBlocked(pulseSelectionGeometryBlocked)
        return 'blocked'
      }

      if (activeIsolationGroupId) {
        const mgr = internalShapeManagerRef.current
        const childUnderFrozenGroup = selectionHasChildUnderFrozenGroup(
          opts?.textOnly ? [] : shapeSnapshots,
          textSnapshots,
          mgr?.getShapes() ?? []
        )

        if (childUnderFrozenGroup) {
          notifyFrozenGroupChildDeleteBlocked(pulseSelectionGeometryBlocked)
          return 'blocked'
        }
      }

      const { txnId: deleteTxnId, ids: deleteIds } = buildDeleteGestureScope(
        shapeSnapshots,
        textSnapshots
      )

      // Announce the whole doomed set before the managers start removing elements one at a time —
      // see `onDeleteGestureScope`.
      onDeleteGestureScope?.({ txnId: deleteTxnId, ids: deleteIds })

      let deleted = false

      if (!opts?.textOnly && canSelectShapes) {
        const mgr = internalShapeManagerRef.current
        const shapeIds = mgr?.getSelectedShapeIds() ?? []

        if (shapeIds.length > 0) {
          mgr?.deleteSelectedShape({ historyTxnId: deleteTxnId })
          deleted = true
        }
      }

      return deleted ? 'deleted' : 'none'
    },
    [
      activeIsolationGroupId,
      buildDeleteGestureScope,
      canSelectShapes,
      getCurrentSelectionSnapshots,
      isTextEditSessionActive,
      onDeleteGestureScope,
      pulseSelectionGeometryBlocked,
      internalShapeManagerRef,
    ]
  )

  return { deleteSelectedObjects }
}
